import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import UIIcon from "common/blocks/UIIcon";
import useModalContext from "common/providers/modal/useModalContext";
import { includedExtrasModal, packModal } from "features/vehicleDetails/modals";
import formatCurrency from "utils/formatCurrency";

import styles from "./IncludedExtras.module.scss";

const cx = classNames.bind(styles);

export type Extra = {
    text: string;
    contents?: string[];
    price: number;
};

export type IncludedExtrasProps = {
    title: string;
    totalPrice: number;
    priceText: string;
    viewPackContentsText: string;
    moreIncludedExtrasText: string;
    seeAllButtonText: string;
    isInsideModal?: boolean;
    extras: Extra[];
};

export const IncludedExtras = (props: IncludedExtrasProps) => {
    const { usePopup, useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();
    const { openPopup } = usePopup();

    const {
        title,
        totalPrice,
        priceText,
        extras,
        viewPackContentsText,
        moreIncludedExtrasText,
        seeAllButtonText,
        isInsideModal,
    } = props;

    if (!extras?.length) return null;

    const nonRenderedExtras = extras.slice(1, -1).sort((a, b) => a?.price ?? 0 - b?.price ?? 0);
    const renderedExtras = isInsideModal
        ? extras
        : extras.slice(0, 2).sort((a, b) => a?.price ?? 0 - b?.price ?? 0);

    const handleOpenSidebar = () => {
        openSidebar(includedExtrasModal(props));
    };

    const handleOpenPackPopup = (extra: Extra) => {
        openPopup(
            packModal({
                title: extra.text,
                extra,
                iconClassName: cx("icon"),
            })
        );
    };

    return (
        <div id="included-extras" className={cx("includedExtras")}>
            {!isInsideModal && (
                <div className={cx("titleRow")}>
                    <h4 className={cx("title")}>{title}</h4>
                    {!!nonRenderedExtras.length && (
                        <Button
                            onClick={handleOpenSidebar}
                            theme="textLink"
                            ariaLabel={seeAllButtonText}
                        >
                            {seeAllButtonText}
                        </Button>
                    )}
                </div>
            )}
            <div className={cx("summary")}>
                <figure className={cx("costOfExtrasIcon")}>
                    <UIIcon icon="SmallStar" />
                </figure>
                <div className={cx("price")}>
                    <span
                        className={cx("bigPrice")}
                        dangerouslySetInnerHTML={{
                            __html: formatCurrency(totalPrice ?? 0, 0, true),
                        }}
                    />
                    <span className={cx("bigPriceText")}>{priceText}</span>
                </div>
            </div>
            <div className={cx("extras", { last: !nonRenderedExtras.length && !isInsideModal })}>
                {renderedExtras.map((extra) => (
                    <div className={cx("extra")} key={extra.text}>
                        {extra?.text && <div className={cx("extraText")}>{extra.text}</div>}
                        {extra?.price && (
                            <div
                                className={cx("extraPrice")}
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(extra.price, 0, true),
                                }}
                            />
                        )}
                        {!!extra.contents?.length && (
                            <div className={cx("extraPackButton")}>
                                <Button
                                    onClick={() => handleOpenPackPopup(extra)}
                                    theme="textLink"
                                    ariaLabel={viewPackContentsText}
                                >
                                    {viewPackContentsText}
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {!!nonRenderedExtras.length && !isInsideModal && (
                <div className={cx("buttonRow")}>
                    <Button
                        onClick={handleOpenSidebar}
                        theme="textLink"
                        ariaLabel={`+${nonRenderedExtras.length} ${moreIncludedExtrasText}`}
                    >
                        +{nonRenderedExtras.length} {moreIncludedExtrasText}
                    </Button>
                </div>
            )}
        </div>
    );
};
